<template>
  <div class="dropdown-overlay" ref="refRoot" tabindex="0" @mousedown="onMouseDown" @mouseup="onMouseUp">
    <div ref="dropdownRef" class="dropdown-content">
      <slot></slot>
      <!-- 드랍다운콘텐츠 -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, nextTick, watch } from 'vue';

import { useKeyPress } from '@/composables/shared/useKeyPress';

const props = defineProps({
  targetEl: {
    type: HTMLElement,
    default: null,
  },
  isVisible: {
    type: Boolean,
  },
});
const emit = defineEmits(['close']);

const refRoot = ref<HTMLElement | null>(null);
const dropdownRef = ref(null);
let mouseDownTarget: EventTarget | null = null;

const onMouseDown = (e: MouseEvent) => {
  mouseDownTarget = e.target;
};

const onMouseUp = (e: MouseEvent) => {
  if (e.target === e.currentTarget && mouseDownTarget === e.currentTarget) {
    closeDropdown();
  }
};

const closeDropdown = () => {
  emit('close');
};

const updateDropdownPosition = () => {
  nextTick(() => {
    if (dropdownRef.value && props.targetEl) {
      const targetRect = props.targetEl.getBoundingClientRect();
      const dropdownRect = dropdownRef.value.getBoundingClientRect();

      /* 가운데센터정렬 */
      let left = targetRect.left + targetRect.width / 2 - dropdownRect.width / 2;
      left = Math.max(12, Math.min(left, window.innerWidth - dropdownRect.width - 12));

      const rightEdge = window.innerWidth - dropdownRect.width - 12;
      left = Math.min(left, rightEdge);
      left = Math.max(left, 12);

      /* 타겟엘레먼트에서16px위에정렬 */
      let top = targetRect.top - dropdownRect.height - 16;
      top = Math.max(12, Math.min(top, window.innerHeight - dropdownRect.height - 12));

      const bottomEdge = window.innerHeight - dropdownRect.height - 12;
      top = Math.min(top, bottomEdge);
      top = Math.max(top, 12);

      dropdownRef.value.style.top = `${top}px`;
      dropdownRef.value.style.left = `${left}px`;

      /* 드랍다운이상단에닿을때스크롤 */
      const maxHeight = targetRect.top - 8 - 16;
      dropdownRef.value.style.maxHeight = `${maxHeight}px`;
      dropdownRef.value.style.overflowY = 'auto';

      dropdownRef.value.scrollTop = dropdownRef.value.scrollHeight;
    }
  });
};
useKeyPress(refRoot, {
  Escape: () => {
    emit('close');
  },
});
onMounted(() => {
  refRoot.value?.focus();
  document.body.style.overflow = 'hidden';
  window.addEventListener('resize', updateDropdownPosition);
  updateDropdownPosition();
});

onUnmounted(() => {
  document.body.style.overflow = '';
  window.removeEventListener('resize', updateDropdownPosition);
});

watch(
  () => props.isVisible,
  (newVal) => {
    if (newVal) updateDropdownPosition();
  }
);
</script>

<style scoped lang="scss">
.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: $z-dropdown;
}

.dropdown-content {
  position: absolute;
  width: fit-content;
  min-width: 17rem;
  max-width: 22rem;
  height: fit-content;
  padding: 1rem;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: var(--surface-overlay);
  box-shadow: $shadow-dropdown;
  overflow-y: auto;
  max-height: calc(100vh - 180px) !important;
  @include respond-to(mobile-portrait) {
    max-width: unset;
    width: calc(100dvw - 1.5rem);
  }
}

@media (hover: none) and (pointer: coarse) {
  .dropdown-content {
    margin-bottom: env(safe-area-inset-bottom, 3rem);
  }
}
</style>

<style lang="scss">
.dropdown-content:has(.floater-active) {
  overflow-y: hidden !important;
}
</style>
